import React, { useState, useCallback } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Stack,
  Typography,
  TextField,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AgeGroupMultiInput } from "./editors/AgeGroupMultiInput";
import { SlotAgeGroupsEditor } from "./editors/SlotAgeGroupsEditor";
import { DayOfWeekTimesEditor } from "./editors/DayOfWeekTimesEditor";
import { DayOfWeekDateRangesEditor } from "./editors/DayOfWeekDateRangesEditor";
import { DateEndOverridesEditor } from "./editors/DateEndOverridesEditor";
import { HolidaysEditor } from "./editors/HolidaysEditor";
import { ExtraDatesEditor } from "./editors/ExtraDatesEditor";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  buildTimeSlotRanges,
  getSessionSlotIncrementMinutes,
} from "../../../utils/programTimeSlotUtils";

interface AdvancedOptionsProps {
  form: any;
  setForm: (form: any) => void;
  usePerDayTimes: boolean;
  setUsePerDayTimes: (value: boolean) => void;
  usePerDayDates: boolean;
}

export const AdvancedOptions = ({
  form,
  setForm,
  usePerDayTimes,
  setUsePerDayTimes,
  usePerDayDates,
}: AdvancedOptionsProps) => {
  // Local drafts so typing in slot key doesn't remount the field and steal focus
  const [slotDrafts, setSlotDrafts] = useState<Record<string, string>>({});
  
  // State for adding new slot capacity
  const [newDay, setNewDay] = useState<string>("");
  const [newSlot, setNewSlot] = useState<string>("");
  const [newCapacity, setNewCapacity] = useState<number | string>("");
  
  // Weekday names to match backend dayOfWeekTimes keys (lowercase)
  const weekdayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

  // Helper to build time slots for a given day key based on chosen granularity for capacities
  const buildTimeSlotsForDay = useCallback((dayKey: string): string[] => {
    const sessionSlotType = (form as any).slotCapacityGranularity || "hourly";
    const dayTimes = form.dayOfWeekTimes?.[dayKey];

    let effectiveStartTime: string | undefined;
    let effectiveEndTime: string | undefined;

    if (dayTimes?.startTime && dayTimes?.endTime) {
      effectiveStartTime = dayTimes.startTime;
      effectiveEndTime = dayTimes.endTime;
    } else if (form.startDate && form.endDate) {
      const globalStart = new Date(form.startDate);
      const globalEnd = new Date(form.endDate);
      effectiveStartTime = `${String(globalStart.getHours()).padStart(2, '0')}:${String(globalStart.getMinutes()).padStart(2, '0')}`;
      effectiveEndTime = `${String(globalEnd.getHours()).padStart(2, '0')}:${String(globalEnd.getMinutes()).padStart(2, '0')}`;
    } else {
      return [];
    }

    if (!effectiveStartTime || !effectiveEndTime) return [];

    const incrementMinutes = getSessionSlotIncrementMinutes(sessionSlotType);
    if (incrementMinutes !== null) {
      return buildTimeSlotRanges(effectiveStartTime, effectiveEndTime, incrementMinutes);
    }

    return [`${effectiveStartTime}-${effectiveEndTime}`];
  }, [(form as any).slotCapacityGranularity, form.dayOfWeekTimes, form.startDate, form.endDate]);

  // Parse slot capacity key to extract day and slot
  const parseSlotCapacityKey = (key: string) => {
    const parts = key.split('_');
    if (parts.length === 2) {
      return { day: parts[0], slot: parts[1] };
    }
    // Fallback for old format (just slot)
    return { day: '', slot: key };
  };

  // Create slot capacity key from day and slot
  const createSlotCapacityKey = (day: string, slot: string) => {
    return day ? `${day}_${slot}` : slot;
  };

  // Handle adding new slot capacity
  const handleAddSlotCapacity = useCallback(() => {
    if (newDay && newSlot && newCapacity !== "" && !isNaN(Number(newCapacity))) {
      const key = createSlotCapacityKey(newDay, newSlot);
      const next = { ...(form.slotCapacities || {}) } as any;
      next[key] = Number(newCapacity);
      setForm({ ...form, slotCapacities: next });
      
      // Reset input fields
      setNewDay("");
      setNewSlot("");
      setNewCapacity("");
    }
  }, [newDay, newSlot, newCapacity, form, setForm]);

  const clearAllPerDayTimes = useCallback(() => {
    setForm({ ...form, dayOfWeekTimes: {} } as any);
  }, [form, setForm]);
  
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" fontWeight={600}>
          Advanced Options
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={3}>
          <Box>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Age Groups
            </Typography>
            <AgeGroupMultiInput
              value={form.ageGroups}
              onChange={(v) => setForm({ ...form, ageGroups: v })}
            />
          </Box>

          <Box>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Slot-specific Age Groups
            </Typography>
            <SlotAgeGroupsEditor
              value={form.slotAgeGroups}
              onChange={(v) => setForm({ ...form, slotAgeGroups: v })}
            />
          </Box>

          <Box>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Button
                size="small"
                variant={usePerDayTimes ? "contained" : "outlined"}
                onClick={() => setUsePerDayTimes(!usePerDayTimes)}
              >
                {usePerDayTimes
                  ? "Using per-day times"
                  : "Use per-day times"}
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => clearAllPerDayTimes()}
              >
                Clear All
              </Button>
            </Stack>
            {usePerDayTimes && (
              <DayOfWeekTimesEditor
                value={form.dayOfWeekTimes}
                onChange={(v) =>
                  setForm({ ...form, dayOfWeekTimes: v })
                }
              />
            )}
          </Box>

          {usePerDayDates && (
            <Box>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Per-Day Date Ranges
              </Typography>
              <DayOfWeekDateRangesEditor
                value={form.dayOfWeekDateRanges}
                onChange={(v) =>
                  setForm({ ...form, dayOfWeekDateRanges: v })
                }
              />
            </Box>
          )}

          {/* Per-slot capacities */}
          <Box>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Per-slot Capacities (optional)
            </Typography>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Optional overrides per day and time slot (format: HH:mm-HH:mm). Example: Monday 18:00-19:00 → 4. This is separate from the session-wide capacity set above.
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
              <FormControl size="small" sx={{ width: 220 }}>
                <InputLabel>Capacity slot size</InputLabel>
                <Select
                  value={(form as any).slotCapacityGranularity || "hourly"}
                  label="Capacity slot size"
                  onChange={(e) => setForm({ ...form, slotCapacityGranularity: e.target.value })}
                >
                  <MenuItem value="hourly">Hourly (default)</MenuItem>
                  <MenuItem value="half_hour">Every 30 minutes</MenuItem>
                  <MenuItem value="two_hour">Every 2 hours</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {Object.entries(form.slotCapacities || {}).map(([key, cap]: any, idx: number) => {
                const { day, slot } = parseSlotCapacityKey(key);
                const availableSlotsForDay = day ? buildTimeSlotsForDay(day) : [];

                return (
                  <Stack key={`${idx}`} direction="row" spacing={1} alignItems="center">
                    <FormControl size="small" sx={{ width: 120 }}>
                      <InputLabel>Day</InputLabel>
                      <Select
                        value={day}
                        label="Day"
                        onChange={(e) => {
                          const newDay = e.target.value as string;
                          const newKey = createSlotCapacityKey(newDay, slot);
                          const next = { ...(form.slotCapacities || {}) } as any;
                          delete next[key];
                          next[newKey] = cap;
                          setForm({ ...form, slotCapacities: next });
                        }}
                      >
                        {weekdayNames.map((d) => (
                          <MenuItem key={d} value={d}>
                            {d.charAt(0).toUpperCase() + d.slice(1)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ width: 200 }}>
                      <InputLabel>Slot (HH:mm-HH:mm)</InputLabel>
                      <Select
                        value={slot}
                        label="Slot (HH:mm-HH:mm)"
                        onChange={(e) => {
                          const newSlot = e.target.value as string;
                          const newKey = createSlotCapacityKey(day, newSlot);
                          const next = { ...(form.slotCapacities || {}) } as any;
                          delete next[key];
                          next[newKey] = cap;
                          setForm({ ...form, slotCapacities: next });
                        }}
                        disabled={!day}
                      >
                        {availableSlotsForDay.length > 0 ? (
                          availableSlotsForDay.map((s) => (
                            <MenuItem key={s} value={s}>{s}</MenuItem>
                          ))
                        ) : (
                          <MenuItem value="" disabled>No slots available</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      type="number"
                      label="Capacity"
                      value={cap}
                      onChange={(e) => {
                        const next = { ...(form.slotCapacities || {}) } as any;
                        next[key] = Math.max(0, Number(e.target.value || 0));
                        setForm({ ...form, slotCapacities: next });
                      }}
                      sx={{ width: 120 }}
                      inputProps={{ min: 0 }}
                    />
                    <IconButton
                      aria-label="delete slot capacity"
                      size="small"
                      onClick={() => {
                        const next = { ...(form.slotCapacities || {}) } as any;
                        delete next[key];
                        setForm({ ...form, slotCapacities: next });
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                );
              })}
              {/* Input fields for adding a new slot capacity */}
              <Stack direction="row" spacing={1} alignItems="center">
                <FormControl size="small" sx={{ width: 120 }}>
                  <InputLabel>Day</InputLabel>
                  <Select
                    value={newDay}
                    label="Day"
                    onChange={(e) => {
                      setNewDay(e.target.value as string);
                      setNewSlot(""); // Reset slot when day changes
                    }}
                  >
                    {weekdayNames.map((d) => (
                      <MenuItem key={d} value={d}>
                        {d.charAt(0).toUpperCase() + d.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ width: 200 }}>
                  <InputLabel>Slot (HH:mm-HH:mm)</InputLabel>
                  <Select
                    value={newSlot}
                    label="Slot (HH:mm-HH:mm)"
                    onChange={(e) => setNewSlot(e.target.value as string)}
                    disabled={!newDay}
                  >
                    {newDay && buildTimeSlotsForDay(newDay).length > 0 ? (
                      buildTimeSlotsForDay(newDay).map((s) => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled>No slots available</MenuItem>
                    )}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  type="number"
                  label="Capacity"
                  value={newCapacity}
                  onChange={(e) => setNewCapacity(e.target.value)}
                  sx={{ width: 120 }}
                  inputProps={{ min: 0 }}
                />
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddSlotCapacity}
                  disabled={!newDay || !newSlot || newCapacity === "" || isNaN(Number(newCapacity))}
                  sx={{ py: 1.2 }}
                >
                  Add
                </Button>
              </Stack>
            </Stack>
          </Box>
                                                              
          {/* <Box>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Date-specific End Overrides
            </Typography>
            <DateEndOverridesEditor
              value={form.dateEndOverrides}
              onChange={(v) =>
                setForm({ ...form, dateEndOverrides: v })
              }
            />
          </Box> */}

          {/* <Box>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Holidays (skip days)
            </Typography>
            <HolidaysEditor
              value={form.holidays}
              onChange={(v) => setForm({ ...form, holidays: v })}
            />
          </Box>

          <Box>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Extra Days
            </Typography>
            <ExtraDatesEditor
              value={form.extraDates}
              onChange={(v) => setForm({ ...form, extraDates: v })}
            />
          </Box> */}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
